import styled, { css, keyframes } from "styled-components";

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    flex-shrink: 0;
`;

// ─── Outer wrapper ────────────────────────────────────────────────────────────

export const SurveyOuter = styled.div`
    margin-bottom: 15px;
    width: 100%;

    @media (min-width: 768px) {
        margin-bottom: 50px;
    }
`;

// ─── White card ───────────────────────────────────────────────────────────────

export const SurveyCard = styled.div`
    background-color: #fff;
    border: 1px solid #e6e6e6;
    border-radius: 0.25rem;
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.05);

    @media (min-width: 768px) {
        margin-top: 30px;
        margin-bottom: 40px;
    }

    @media (max-width: 767px) {
        border-color: transparent !important;
        box-shadow: none !important;
    }
`;

// ─── Progress indicator ───────────────────────────────────────────────────────

interface ProgressSectionProps { step: 1 | 2 | 3; }

export const    ProgressSection = styled.div<ProgressSectionProps>`
    text-align: center;
    margin-top: 0;
    margin-bottom: 0;
    position: relative;

    /* full-width gray track */
    &::before {
        content: '';
        position: absolute;
        top: 14px;
        left: 0;
        right: 0;
        height: 3px;
        background: #d9d9d9;
        z-index: 0;

        @media (max-width: 767px) {
            top: 12px;
        }
    }

    /* red progress fill from left edge to current step's indicator center */
    &::after {
        content: '';
        position: absolute;
        top: 14px;
        left: 0;
        height: 3px;
        background: #fd3300;
        z-index: 1;
        width: ${({ step }) =>
            step === 1 ? 'calc(50% - 220px)' :
            step === 2 ? '50%' :
            '100%'
        };

        @media (max-width: 767px) {
            top: 12px;
            width: ${({ step }) =>
                step === 1 ? '55px' :
                step === 2 ? '50%' :
                '100%'
            };
        }
    }

    @media (min-width: 768px) {
        margin-top: 55px;
        margin-bottom: 10px;
    }

    @media (max-width: 767px) {
        margin-bottom: 40px !important;
    }
`;

export const ProgressBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    max-width: 495px;
    margin: 0 auto;
    position: relative;
    z-index: 2;
    padding-bottom: 50px;

    @media (max-width: 767px) {
        padding-bottom: 45px;
    }
`;

interface ProgressItemProps { state: 'inactive' | 'active' | 'done'; }

const checkmarkSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M1.5 6L4.5 9L10.5 3' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

export const ProgressItem = styled.div<ProgressItemProps>`
    position: relative;
    font-weight: 400;
    color: ${({ state }) => (state === 'inactive' ? '#979797' : '#1d1a1a')};

    &:first-child {
        @media (max-width: 767px) {
            margin-left: 40px;
        }
    }
    &:last-child {
        @media (max-width: 767px) {
            margin-right: 40px;
        }
    }
`;

export const ProgressItemIcon = styled.div<ProgressItemProps>`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;
    font-size: 16px;
    line-height: 1;
    font-weight: 700;
    pointer-events: none;
    position: relative;
    user-select: none;

    background: ${({ state }) => (state === 'inactive' ? '#bfbfbf' : '#fd3300')};
    color: ${({ state }) => (state === 'inactive' ? '#fff' : '#fd3300')};

    /* active: white dot inside */
    ${({ state }) =>
        state === 'active' &&
        css`
            &::before {
                content: '';
                width: 10px;
                height: 10px;
                background: #fff;
                border-radius: 50%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                @media (max-width: 767px) {
                    width: 6px;
                    height: 6px;
                }
            }
        `}

    /* done: checkmark inside */
    ${({ state }) =>
        state === 'done' &&
        css`
            &::before {
                content: '';
                width: 14px;
                height: 14px;
                background: ${checkmarkSvg} no-repeat center center;
                background-size: contain;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }
        `}
`;

export const ProgressItemLabel = styled.div`
    font-family: 'Repro', sans-serif;
    font-size: 20px;
    line-height: normal;
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;

    @media (max-width: 768px) {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
    }
`;

// ─── Step content area ────────────────────────────────────────────────────────

export const StepContent = styled.div`
    padding-bottom: 0;

    @media (min-width: 768px) {
        padding-bottom: 55px;
    }
`;

export const StepContainer = styled.div`
    max-width: 960px;
    margin: 0 auto;
    padding: 0;

    @media (min-width: 768px) {
        padding: 0 15px;
    }
`;

// ─── Vimeo / aspect-ratio 16:9 box ────────────────────────────────────────────

export const AspectRatioBox = styled.div`
    position: relative;
    width: 100%;
    max-width: 960px;
    height: 0;
    padding-bottom: 56.25%; /* 16:9 */

    iframe,
    video {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
    }
`;

// ─── Step 1 footer (icon + text + upload button) ──────────────────────────────

export const StepFooter = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 35px;

    @media (min-width: 768px) {
        flex-direction: row;
        margin-top: 55px;
    }
`;

export const StepFooterText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const CheckIcon = styled.span`
    display: none;
    width: 36px;
    height: 36px;
    background: url("https://members.performancegolf.com/wp-content/themes/pgm/dist/images/icons/check-gray.svg") no-repeat center center;
    background-size: contain;

    @media (min-width: 768px) {
        display: block;
    }
`;

export const StepFooterDescription = styled.div`
    font-family: 'Repro', sans-serif;
    font-size: 20px;
    line-height: 20px;
    margin-top: -10px;

    @media (max-width: 768px) {
        font-size: 16px;
        line-height: 20px;
        text-align: left;
    }
`;

export const UploadButtonWrapper = styled.div`
    flex-shrink: 0;
    margin-top: 25px;
    width: 100%;

    @media (min-width: 768px) {
        margin-top: 0;
        padding: 0 0 0 60px;
        width: auto;
    }
`;

export const HiddenFileInput = styled.input`
    display: none;
`;

const redButtonBase = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    background-color: #fd3300;
    border: 1px solid #fd3300;
    border-radius: 100px;
    padding: 14px 20px;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.2s, border-color 0.2s;
    width: 100%;

    @media (min-width: 768px) {
        width: auto;
    }

    &:hover {
        background-color: #e62a00;
        border-color: #cc2900;
        color: #fff;
    }

    &:focus,
    &:active {
        background-color: #cc2900;
        border-color: #b22700;
        color: #fff;
        outline: none;
        box-shadow: 0 0 0 3px rgba(253, 51, 0, 0.25);
    }

    &:disabled {
        background-color: #bfbfbf;
        border-color: #bfbfbf;
        cursor: not-allowed;
        opacity: 1;
    }

    /* upload arrow icon */
    &::before {
        content: '';
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        display: inline-block;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M7 12V3.85L4.4 6.45L3 5L8 0L13 5L11.6 6.45L9 3.85V12H7ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z' fill='white'/%3E%3C/svg%3E") no-repeat center center;
        background-size: contain;
    }
`;

export const UploadLabel = styled.label`
    ${redButtonBase}
    padding: 12px;
`;

export const RedButton = styled.button`
    ${redButtonBase}
    border: none;

    &:disabled {
        background-color: #bfbfbf;
        border-color: #bfbfbf;
        cursor: not-allowed;
    }
`;

// ─── Step 2 specific ──────────────────────────────────────────────────────────

export const VideoWrapper = styled.div`
    margin-bottom: 30px;

    video {
        background: #000;
        display: block;
        width: 100%;
        max-height: 40vh;

        @media (max-width: 767px) {
            max-height: 30vh;
        }
    }
`;

export const FileRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    @media (max-width: 767px) {
        justify-content: center;
    }
`;

export const FileCheckIcon = styled.span`
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    background: url("https://members.performancegolf.com/wp-content/themes/pgm/dist/images/icons/check-gray.svg") no-repeat center center;
    background-size: contain;
    display: inline-block;
`;

export const FileName = styled.span`
    font-family: 'Repro', sans-serif;
    font-size: 21px;
    font-weight: 400;
    color: #1d1a1a;

    @media (max-width: 767px) {
        font-size: 16px;
    }
`;

export const RemoveLink = styled.a`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #fd3300;
    text-decoration: none;
    margin-left: 4px;

    &:hover {
        text-decoration: underline;
        color: #e62a00;
    }
`;

export const StepActions = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 30px;
    gap: 20px;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-end;
        gap: 0;
    }
`;

export const StepInfo = styled.div`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    line-height: 20px;
    text-align: center;
    color: rgb(10, 10, 10);

    @media (min-width: 768px) {
        font-size: 21px;
        line-height: 1.24;
        text-align: left;
    }

    p {
        margin-bottom: 0.5em;
        &:last-child { margin-bottom: 0; }
    }

    @media (max-width: 768px) {
        margin-bottom: 25px;
    }
`;

export const ActionButtons = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
    width: 100%;
    gap: 4px;

    @media (min-width: 768px) {
        width: auto;
        padding-left: 60px;
        justify-content: center;
    }
`;

export const BackLink = styled.a`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    color: #7b726c;
    text-decoration: none;
    padding: 14px 15px;
    flex-shrink: 0;

    &:hover {
        color: #1d1a1a;
    }
`;

// ─── Step 3 specific ──────────────────────────────────────────────────────────

export const StepHeading = styled.div`
    font-family: 'Repro', sans-serif;
    font-size: 22px;
    font-weight: 500;
    line-height: 1;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 30px;
    margin-top: 10px;

    @media (min-width: 768px) {
        font-size: 26px;
        line-height: 1.27;
        margin-bottom: 35px;
        margin-top: 0;
    }
`;

export const FormBackground = styled.div`
    background: #f3f4f7;
    padding: 20px;
    margin: 0 -15px;

    @media (min-width: 768px) {
        margin: 0;
    }
`;

export const FormContainer = styled.div`
    max-width: 645px;
    margin: 0 auto;
    padding: 0 15px;

    @media (max-width: 768px) {
        padding: 0;
    }
`;

export const FormRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;

    @media (min-width: 768px) {
        flex-direction: row;
        gap: 24px;
    }
`;

export const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const FormLabel = styled.label`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #1d1a1a;
    display: inline-block;
    margin-bottom: 6px;
`;

export const SelectWrapper = styled.div`
    position: relative;

    &::before {
        content: '';
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        width: 11px;
        height: 7px;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7' fill='none'%3E%3Cpath d='M1 1L5.5 6L10 1' stroke='%231D1A1A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center center no-repeat;
        background-size: contain;
        pointer-events: none;
        z-index: 1;
    }
`;

export const StyledSelect = styled.select`
    display: block;
    width: 100%;
    background: #ffffff;
    border: 1px solid #cecccc;
    border-radius: 7px;
    padding: 15px 36px 15px 12px;
    font-family: 'Repro', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #18181b;
    -webkit-appearance: none;
    appearance: none;
    outline: none;
    cursor: pointer;

    &:focus {
        border-color: #fd3300;
    }
`;

export const TextareaWrapper = styled.div`
    position: relative;
    margin-bottom: 20px;
`;

export const StyledTextarea = styled.textarea`
    display: block;
    width: 100%;
    min-height: 180px;
    background: #ffffff;
    border: 1px solid #cecccc;
    border-radius: 7px;
    padding: 10px;
    font-family: 'Repro', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
    color: #18181b;
    resize: vertical;
    outline: none;

    &:focus {
        border-color: #fd3300;
    }

    @media (max-width: 767px) {
        font-size: 16px;
    }
`;

export const FormFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    width: 100%;
    padding-left: 20px;

    @media (min-width: 768px) {
        padding-left: 0;
    }
`;

export const SubmitArea = styled.div`
    text-align: end;
    flex-grow: 1;

    @media (min-width: 768px) {
        flex-grow: 0;
        padding-left: 25px;
    }
`;

export const SubmitButton = styled.button`
    font-family: 'Repro', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    background-color: #fd3300;
    border: 1px solid #fd3300;
    border-radius: 100px;
    padding: 14px 20px;
    cursor: pointer;
    width: 100%;
    transition: background-color 0.2s, border-color 0.2s;

    @media (min-width: 768px) {
        width: auto;
    }

    @media (max-width: 768px) {
        max-width: 210px;
    }

    &:hover:not(:disabled) {
        background-color: #e62a00;
        border-color: #cc2900;
    }

    &:focus:not(:disabled),
    &:active:not(:disabled) {
        background-color: #cc2900;
        border-color: #b22700;
        outline: none;
        box-shadow: 0 0 0 3px rgba(253, 51, 0, 0.25);
    }

    &:disabled {
        background-color: #a5a3a3;
        border-color: #a5a3a3;
        color: #757575;
        cursor: not-allowed;
        opacity: 1;
    }
`;

export const BreakWordText = styled.br`
    display: block;
    @media (max-width: 768px) {
        display: none;
    }
`;

export const BreakDownMobile = styled.br`
    display: none;
    @media (max-width: 768px) {
        display: block;
    }
`;
